import { BodyLarge, BodyMedium, BodySmall, BoxSmallLabel, Checkbox, theme } from '@arnold/common';
import styled from '@emotion/styled/macro';
import { useTranslation } from 'react-i18next';
import {
  OrganizationQuery,
  SurveyGroupRespondentImportType,
  SurveyGroupRespondentsQuery,
  SurveyGroupWithoutRespondentsQuery,
  SurveyStatus,
  UserQuery,
} from '../../generated/hooks';
import DateInput from '../Common/DateInput';
import SurveyGroupChooseInstanceType from '../Forms/SurveyGroupChooseInstanceType';
import { DateSelector, DateSelectorInputGroup, ExplanatoryList } from '../StyledComponents';
import { getDataSource, SurveyGroupSurveysPreviewTable } from './SurveyGroupSurveysPreviewTable';

export type EditParticipant = {
  id: string;
  firstname: string;
  surname: string;
  deleted: boolean;
  addedOn: string | null;
  alreadyStartedSurveys?: NonNullable<
    NonNullable<SurveyGroupRespondentsQuery['surveyGroupRespondents']['data']>[0]
  >['surveys'];
  type?: SurveyGroupRespondentImportType;
  creditsToReturn?: number;
  channels?: NonNullable<NonNullable<SurveyGroupRespondentsQuery['surveyGroupRespondents']['data']>[0]>['channels'];
};

type EditParticipantFormProps = {
  surveyGroup: NonNullable<SurveyGroupWithoutRespondentsQuery['surveyGroup']>;
  respondent: EditParticipant;
  setRespondent: (respondent: EditParticipant | null) => void;
  organization: UserQuery['user']['organization'] | OrganizationQuery['organization'];
  addedOn: Date | null;
  setAddedOn: (date: Date | null) => void;
  runOngoingSteps: string[];
  onOngoingPhaseChange: (actualStepId: string) => () => void;
};

const GreySmallText = styled(BodySmall)`
  color: ${theme.colors.text.secondary};
`;

const EditParticipantForm = ({
  surveyGroup,
  respondent,
  setRespondent,
  organization,
  addedOn,
  setAddedOn,
  runOngoingSteps,
  onOngoingPhaseChange,
}: EditParticipantFormProps) => {
  const { t, i18n } = useTranslation('surveyGroup');

  if (!respondent) {
    return null;
  }
  return (
    <>
      <BodyLarge margin={`0 0 ${theme.spacing.f} 0`}>
        <strong>
          {respondent.firstname} {respondent.surname}
        </strong>{' '}
        {respondent.alreadyStartedSurveys &&
          respondent.alreadyStartedSurveys.length > 0 &&
          t('surveyGroupAddParticipants:alreadyAdded', {
            count: respondent.alreadyStartedSurveys.length,
            postProcess: 'interval',
          })}
      </BodyLarge>
      <BoxSmallLabel data-icom={'label-survey-group-added-on'}>{t('startDate')}</BoxSmallLabel>
      <DateSelectorInputGroup>
        <DateSelector>
          <DateInput
            onDateChange={(value) => setAddedOn(new Date(value.toISOString().substring(0, 10) + 'T00:00:00Z'))}
            inputName={'addedOn'}
            placeholder={t('selectDate')}
            date={addedOn?.toISOString()}
          />
        </DateSelector>
      </DateSelectorInputGroup>

      {(!respondent.alreadyStartedSurveys || respondent.alreadyStartedSurveys?.length === 0) && (
        <>
          <GreySmallText>{t('surveyGroupEditParticipant:nothingSend')}</GreySmallText>
          <ExplanatoryList>
            <li>
              <BodySmall>{t('surveyGroupEditParticipant:nothingSendExplanation')}</BodySmall>
            </li>
          </ExplanatoryList>
        </>
      )}
      {respondent.alreadyStartedSurveys && respondent.alreadyStartedSurveys.length > 0 && (
        <>
          <SurveyGroupChooseInstanceType
            respondent={respondent}
            setRespondentRunType={(_id, type) => {
              setRespondent({ ...respondent, type });
            }}
          />
        </>
      )}

      {addedOn && (
        <>
          <SurveyGroupSurveysPreviewTable
            topicGroup={surveyGroup.topicGroup}
            alreadyStartedSurveys={
              respondent.type === SurveyGroupRespondentImportType.CreateNewRun
                ? undefined
                : respondent.alreadyStartedSurveys
            }
            addedOn={addedOn}
            productHistory={organization?.productVersionHistory ?? []}
          />
          <div className="mt-8 mb-8">
            {getDataSource(
              surveyGroup.topicGroup,
              addedOn!,
              i18n.language,
              t,
              organization?.productVersionHistory,
              respondent.type === SurveyGroupRespondentImportType.CreateNewRun
                ? undefined
                : respondent.alreadyStartedSurveys,
            )
              .filter((row) => row.rawStatus === SurveyStatus.Ongoing && !row.foundStartedSurvey)
              .map((row) => {
                return (
                  <Checkbox
                    key={row.id}
                    selected={runOngoingSteps.includes(row.id)}
                    onToggle={onOngoingPhaseChange(row.id)}
                    text={
                      <BodyMedium>
                        {t('addSurveyGroupRespondentModal:ongoingPhase')} <strong>{row.topic}</strong>
                      </BodyMedium>
                    }
                  />
                );
              })}
          </div>
        </>
      )}
    </>
  );
};

export default EditParticipantForm;
